/* eslint-disable @typescript-eslint/no-explicit-any -- legacy PDF/stuffing response shapes */
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { FileExport } from "components/UI/Icons";
import { ARROW_POSITION, Tooltip, TooltipContent } from "components/UI/Tooltip";
import { COLORS } from "constants/colors.constants";
import { ImageContext } from "pages/StuffingPage";
import { ResponseContainer, RootState } from "store";
import { exportStuffingPdf } from "utils/exportStuffingPdf";
import { getReqBody, stringify } from "utils/helper";
import { ProductsValueType } from "utils/utils";
import { RouteType } from "../../../App";
import styles from "./FooterNav.module.scss";

interface FooterNavProps {
  page: string;
  linkBack: RouteType;
  linkNext?: RouteType;
  setRoute: (page: RouteType) => void;
  generatePdf?: () => Promise<void>;
  loadMore?: boolean;
}

export type FirstPageData = {
  title: string;
  qty: number;
  image: string;
  itemQty: number;
  cargoVolume: number;
  cargoWeight: number;
  lengthUnits: "mm" | "m" | string;
};

export type RenderPage = {
  key: string;
  page: number;
  items: ProductsValueType[];
  firstPageData: FirstPageData | null;
};

const COPY_REQUEST_TOOLTIP_TEXT = "Copy request data in JSON format";
const COPIED_TEXT = "Copied";

export const FooterNav = ({ page, setRoute, generatePdf, loadMore }: FooterNavProps) => {
  const { t } = useTranslation();
  const images = useContext(ImageContext);
  const [copyRequestTooltipText, setCopyRequestTooltipText] = useState(COPY_REQUEST_TOOLTIP_TEXT);
  const { response, options, auth, ...rest } = useSelector((state: RootState) => state.root);

  const copyRequestHandler = () => {
    const copyText = getReqBody({ response, options, auth, ...rest });
    navigator.clipboard.writeText(stringify(copyText));
  };

  const exportToPDF = async () => {
    if (!response?.data?.result) return;

    let imagesData = images;

    if (loadMore && generatePdf) {
      await new Promise((resolve) =>
        setTimeout(async () => {
          await generatePdf().then((res: any) => {
            imagesData = { ...imagesData, ...res };
          });
          resolve("");
        }, 0)
      );
    }

    await exportStuffingPdf({
      result: response.data.result as ResponseContainer[],
      cargoAttributes: response.data.cargoAttributes,
      images: imagesData,
      options,
      user: auth?.user,
    });
  };

  return (
    <div className={styles.footerNav}>
      <div className={styles.left} />

      <div className={styles.right}>
        {page === "container" && (
          <Button type="submit" variant={BUTTON_VARIANT.PRIMARY} className={styles.actionBtn}>
            {t("Next")}
          </Button>
        )}

        {page === "product" && (
          <Button
            type="button"
            variant={BUTTON_VARIANT.PRIMARY}
            className={styles.actionBtn}
            onClick={() => setRoute("container")}
          >
            {t("Next")}
          </Button>
        )}

        {page === "stuffing" && response?.data?.result && (
          <>
            <Button type="button" variant={BUTTON_VARIANT.PRIMARY} className={styles.actionBtn} onClick={exportToPDF}>
              <FileExport color={COLORS.WHITE} />
              {t("Export to PDF")}
            </Button>

            <Tooltip
              triggerElement={
                <Button
                  type="button"
                  variant={BUTTON_VARIANT.PRIMARY}
                  className={styles.actionBtn}
                  onClick={() => {
                    copyRequestHandler();
                    setCopyRequestTooltipText(COPIED_TEXT);
                  }}
                  onMouseLeave={() => {
                    if (copyRequestTooltipText === COPY_REQUEST_TOOLTIP_TEXT) return;
                    setTimeout(() => {
                      setCopyRequestTooltipText(COPY_REQUEST_TOOLTIP_TEXT);
                    }, 100);
                  }}
                >
                  {t("Copy request")}
                </Button>
              }
              offsetY={8}
              placement="top"
              enabledOnMobile={true}
            >
              <TooltipContent arrowPosition={ARROW_POSITION.BOTTOM} className={styles.tooltipContent}>
                {t(copyRequestTooltipText)}
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
};
